import React, { useState } from "react";
import { FormattedMessage, injectIntl } from "react-intl";
import UserPermissionsEdit from "./UserPermissionsEdit";
import UserGroupPermissionsEdit from "./UserGroupPermissionsEdit";
import { intlShape } from "../../shapes";
import { isNothing, sortBy, displayUserName } from "../../utils";
import * as browserUtils from "../../utils/browserUtils";
import PermissionType from "../../constants/PermissionType";
import {
  apiShape,
  notifierShape,
  withApi,
  withNotifier,
  withRules,
  rulesShape,
} from "../../context";
import {
  NotFoundError,
  ConflictError,
  BadRequestError,
} from "../../api/errors";

type OwnProps = {
  allowedPermissions?: $TSFixMe[]; // TODO: PropTypes.oneOf([ PermissionType.READ, PermissionType.UPDATE, PermissionType.DELETE, PermissionType.PERMIT, ])
  permitableId: number;
  permitableType: string;
  intl: intlShape;
  targetOnDeleteOwnReadPermission?: string;
  notifier?: notifierShape;
  api?: apiShape;
  rules?: rulesShape;
};

const defaultProps: Partial<OwnProps> = {
  targetOnDeleteOwnReadPermission: "/",
  allowedPermissions: [
    PermissionType.READ,
    PermissionType.UPDATE,
    PermissionType.DELETE,
    PermissionType.PERMIT,
  ],
};

type Props = OwnProps & typeof defaultProps;

export function PermissionsEdit({
  api,
  permitableType,
  permitableId,
  notifier,
  intl,
  targetOnDeleteOwnReadPermission,
  allowedPermissions,
  rules,
}: Props) {
  const [usersPermissions, setUsersPermissions] = useState([]);
  const [userGroupsPermissions, setUserGroupsPermissions] = useState([]);
  const [accessRights, setAccessRights] = useState({});
  const [currentUser, setCurrentUser] = useState({});

  // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
  const acceptConnectUsersInput = rules.application().get("acceptConnectUsers");

  const loadAccessRights = () => {
    const param = {
      permitable_type: permitableType,
      permitable_id: permitableId,
    };
    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
    api
      .get("/users/current/access_rights", param)
      .then((result: $TSFixMe) => {
        setAccessRights(result);
        if (!result.can_read) {
          browserUtils.navigateTo(targetOnDeleteOwnReadPermission);
        }
      })
      .catch(() =>
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        notifier.showError(intl.formatMessage({ id: "api.error.unknown" })),
      );
  };

  const onUserSubmit = async (values: $TSFixMe, actions: $TSFixMe) => {
    actions.setSubmitting(true);
    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
    api
      .get("/users/lookup", { email: values.email })
      .then((user: $TSFixMe) => {
        if (
          usersPermissions.findIndex((u) => user.id === (u as $TSFixMe).id) ===
          -1
        ) {
          // @ts-expect-error TS(2345) FIXME: Argument of type '{ id: any; name: string; read: null; ... Remove this comment to see the full error message
          usersPermissions.push({
            id: user.id,
            name: `${displayUserName(user, true, true)}`,
            email: user.email,
            read: null,
            update: null,
            delete: null,
            permit: null,
          });
          actions.setSubmitting(false);
          actions.resetForm({ values: {} });
        } else {
          actions.setErrors({
            email: intl.formatMessage({
              id: "edit_permissions.user_permissions_already_exist",
            }),
          });
        }
        actions.setSubmitting(false);
      })
      .catch((error: $TSFixMe) => {
        actions.setSubmitting(false);
        if (error instanceof NotFoundError) {
          actions.setErrors({
            email: intl.formatMessage({
              id: "api.error.email.not_found_no_permission",
            }),
          });
        } else {
          // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
          notifier.showError(intl.formatMessage({ id: "api.error.unknown" }));
        }
      });
  };

  const onUserGroupSubmit = async (values: $TSFixMe, actions: $TSFixMe) => {
    actions.setSubmitting(true);
    // @ts-expect-error TS(2345) FIXME: Argument of type '{ id: any; name: any; read: null; ... Remove this comment to see the full error message
    userGroupsPermissions.push({
      id: values.usergroup.id,
      name: values.usergroup.name,
      read: null,
      update: null,
      delete: null,
      permit: null,
    });
    actions.setSubmitting(false);
    actions.resetForm({ values: {} });
  };

  function setUserOrUserGroupPermission(
    permissions: $TSFixMe,
    permission: $TSFixMe,
  ) {
    /*  eslint-disable no-param-reassign */
    // eslint-disable-next-line default-case
    switch (permission.permission_type) {
      case PermissionType.READ:
        permissions.read = permission.id;
        break;
      case PermissionType.UPDATE:
        permissions.update = permission.id;
        break;
      case PermissionType.DELETE:
        permissions.delete = permission.id;
        break;
      case PermissionType.PERMIT:
        permissions.permit = permission.id;
    }
    /* eslint-enable no-param-reassign */
  }

  const loadPermissions = () => {
    const includeables = acceptConnectUsersInput
      ? "assignable, technical_connect_users"
      : "assignable";
    const permissionsFilter = {
      permitable_id: permitableId,
      permitable_type: permitableType,
      per_page: 100,
      include: includeables,
    };
    // permissions are paginated in different way and the best value for per_page parameter is 300
    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
    api
      .getAll("/permissions", {
        ...permissionsFilter,
        per_page: 300,
        include_total_count: false,
      })
      .then((response: $TSFixMe) => {
        const usPermissions: $TSFixMe = [];
        const ugsPermissions: $TSFixMe = [];
        response.permissions.forEach((permission: $TSFixMe) => {
          if (permission.assignable.type === "User") {
            let userPermissions = usPermissions.find(
              // @ts-expect-error TS(7006) FIXME: Parameter 'u' implicitly has an 'any' type.
              (u) => u.id === permission.assignable.id,
            );
            const permissionUser = permission.assignable.user;
            if (!userPermissions) {
              userPermissions = {
                id: permission.assignable.id,
                name: displayUserName(permissionUser, true, true),
                email: permissionUser.email,
                read: null,
                update: null,
                delete: null,
                permit: null,
              };
              usPermissions.push(userPermissions);
            }
            setUserOrUserGroupPermission(userPermissions, permission);
          }
          if (permission.assignable.type === "Usergroup") {
            let userGroupPermissions = ugsPermissions.find(
              // @ts-expect-error TS(7006) FIXME: Parameter 'u' implicitly has an 'any' type.
              (u) => u.id === permission.assignable.id,
            );
            if (!userGroupPermissions) {
              userGroupPermissions = {
                id: permission.assignable.id,
                name: permission.assignable.usergroup.name,
                read: null,
                update: null,
                delete: null,
                permit: null,
              };
              ugsPermissions.push(userGroupPermissions);
            }
            setUserOrUserGroupPermission(userGroupPermissions, permission);
          }
          setUsersPermissions(sortBy(usPermissions, "name"));
          setUserGroupsPermissions(sortBy(ugsPermissions, "name"));
        });
      })
      .catch(() =>
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        notifier.showError(intl.formatMessage({ id: "api.error.unknown" })),
      );
  };

  const loadCurrentUser = () => {
    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
    api
      .get("/users/current", {})
      .then((user: $TSFixMe) => setCurrentUser(user))
      .catch(() =>
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        notifier.showError(intl.formatMessage({ id: "api.error.unknown" })),
      );
  };

  React.useEffect(() => {
    loadAccessRights();
    loadCurrentUser();
    loadPermissions();
  }, []);

  const handlePermissionDeleteError = (errors: $TSFixMe) => {
    if (errors instanceof NotFoundError) {
      // somebody deleted his permit permission in the background, we will reload the access rights for the user
      loadAccessRights();
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      notifier.showError(
        intl.formatMessage({ id: "api.error.permission_delete_no_permission" }),
      );
    } else if (errors instanceof ConflictError) {
      // if the permission is removed no user have the 4 permissions for the resource
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      notifier.showError(
        intl.formatMessage({ id: "api.error.owner_required" }),
      );
    } else {
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      notifier.showError(intl.formatMessage({ id: "api.error.unknown" }));
    }
  };

  const onPermissionDelete = async (
    permissionId: $TSFixMe,
    assignableType: $TSFixMe,
    assignableId: $TSFixMe,
  ) => {
    try {
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      await api.delete(`/permissions/${permissionId}`);
      const neededPermissions =
        assignableType === "User" ? usersPermissions : userGroupsPermissions;
      const existingPermissions = neededPermissions.find(
        (u) => (u as $TSFixMe).id === assignableId,
      );
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      if (existingPermissions.read === permissionId) {
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        existingPermissions.read = null;
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      } else if (existingPermissions.update === permissionId) {
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        existingPermissions.update = null;
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      } else if (existingPermissions.delete === permissionId) {
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        existingPermissions.delete = null;
      } else {
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        existingPermissions.permit = null;
      }
      if (assignableType === "User") {
        setUsersPermissions([...neededPermissions]);
      } else {
        setUserGroupsPermissions([...neededPermissions]);
        loadAccessRights();
      }
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      notifier.showSuccess(
        intl.formatMessage({ id: "edit_permissions.notifications.deleted" }),
      );
    } catch (e) {
      handlePermissionDeleteError(e);
    }
  };

  function remove(neededPermissions: $TSFixMe, id: $TSFixMe, isUser: $TSFixMe) {
    const newPermissions = neededPermissions.filter(
      (u: $TSFixMe) => u.id !== id,
    );
    if (isUser) {
      setUsersPermissions(newPermissions);
    } else {
      setUserGroupsPermissions(newPermissions);
    }
  }

  const onDelete = (id: $TSFixMe, isUser: $TSFixMe) => {
    const neededPermissions = isUser ? usersPermissions : userGroupsPermissions;
    const permissions = neededPermissions.find(
      (u) => (u as $TSFixMe).id === id,
    );
    const existing = [
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      permissions.read,
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      permissions.update,
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      permissions.delete,
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      permissions.permit,
    ].filter((n) => n);
    if (existing.length === 0) {
      remove(neededPermissions, id, isUser);
    } else {
      // we can just delete all permissions by the first id and then remove the user from the list
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      api
        .delete(`/permissions/${existing[0]}`, { all: true })
        .then(() => {
          remove(neededPermissions, id, isUser);
          // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
          notifier.showSuccess(
            intl.formatMessage({
              id: "edit_permissions.notifications.deleted",
            }),
          );
        })
        .catch((e: $TSFixMe) => {
          handlePermissionDeleteError(e);
          loadPermissions();
        });
    }
  };

  const onPermissionCreate = (
    assignableType: $TSFixMe,
    assignableId: $TSFixMe,
    permissionType: $TSFixMe,
  ) => {
    const neededPermissions =
      assignableType === "User" ? usersPermissions : userGroupsPermissions;
    const existingPermissions = neededPermissions.find(
      (u) => (u as $TSFixMe).id === assignableId,
    );
    const addReadPermission =
      permissionType !== PermissionType.READ &&
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      isNothing(existingPermissions.read);

    const newPermission = {
      permission_type: addReadPermission
        ? [PermissionType.READ, permissionType]
        : [permissionType],
      assignable: {
        id: assignableId,
        type: assignableType,
      },
      permitable: {
        id: permitableId,
        type: permitableType,
      },
    };

    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
    api
      .post("/permissions", newPermission)
      .then((response: $TSFixMe) => {
        response.permissions.forEach((permission: $TSFixMe) => {
          setUserOrUserGroupPermission(existingPermissions, permission);
        });
        if (assignableType === "User") {
          setUsersPermissions([...neededPermissions]);
        } else {
          setUserGroupsPermissions([...neededPermissions]);
        }
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        notifier.showSuccess(
          intl.formatMessage({ id: "edit_permissions.notifications.created" }),
          3000,
        );
      })
      .catch((errors: $TSFixMe) => {
        if (errors instanceof BadRequestError && errors.contains("taken")) {
          // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
          notifier.showError(
            intl.formatMessage({
              id: "edit_permissions.user_permissions_already_exist",
            }),
          );
        } else {
          // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
          notifier.showError(intl.formatMessage({ id: "api.error.unknown" }));
        }
      });
  };

  const renderedUserPermissions = (
    <UserPermissionsEdit
      currentUser={currentUser}
      permitableId={permitableId}
      permitableType={permitableType}
      usersPermissions={usersPermissions}
      onPermissionCreate={onPermissionCreate}
      onPermissionDelete={onPermissionDelete}
      onDelete={onDelete}
      onAdd={onUserSubmit}
      // @ts-expect-error TS(2322) FIXME: Type 'any[] | undefined' is not assignable to type... Remove this comment to see the full error message
      allowedPermissions={allowedPermissions}
      acceptConnectUsers={acceptConnectUsersInput}
    />
  );

  const renderedUserGroupPermissions = (
    <UserGroupPermissionsEdit
      userGroupsPermissions={userGroupsPermissions}
      permitableId={permitableId}
      permitableType={permitableType}
      onPermissionCreate={onPermissionCreate}
      onPermissionDelete={onPermissionDelete}
      onDelete={onDelete}
      onAdd={onUserGroupSubmit}
      // @ts-expect-error TS(2322) FIXME: Type 'any[] | undefined' is not assignable to type... Remove this comment to see the full error message
      allowedPermissions={allowedPermissions}
    />
  );

  return accessRights && (accessRights as $TSFixMe).can_permit ? (
    <div>
      <h2 id="edit-permissions">
        <FormattedMessage id="edit_permissions.header" />
      </h2>
      {renderedUserGroupPermissions}
      {renderedUserPermissions}
    </div>
  ) : null;
}

PermissionsEdit.defaultProps = defaultProps;

// @ts-expect-error TS(2345) FIXME: Argument of type 'typeof PermissionsEdit' is not a... Remove this comment to see the full error message
export default injectIntl(withNotifier(withApi(withRules(PermissionsEdit))));
